import { loadSplideCSS, loadSplide, loadSplideAutoScroll } from '../../externals/splide.js';

export const defaults = {};

// ── SplideManager ─────────────────────────────────────────────────────────────

const d = document;

const BP      = { mobileMax: 767, tabletMax: 991 };
const BP_ORDER = ['mobile', 'tablet', 'desktop'];

const DEFAULTS = {
  perPage:     { desktop: 3, tablet: 2, mobile: 1 },
  gap:         '1rem',
  speed:       500,
  rewindSpeed: 300,
  interval:    3000,
  perMove:     1,
};

const ATTR_SPLIDE_ONLY = 'data-splide-class-splideonly';
const ATTR_OTHER_ONLY  = 'data-splide-class-nonsplideonly';

const STATE = {
  assetsPromise:   null,
  instances:       new Map(),
  currentBp:       null,
  resizeBound:     false,
  observer:        null,
  observed:        new Set(),
  resizingLock:    false,
  lastResizeWidth: null,
};

// ── Utils ─────────────────────────────────────────────────────────────────────

const num = (v, fb) => { const n = parseFloat(v);   return Number.isFinite(n) ? n : fb; };
const int = (v, fb) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : fb; };

function boolAttr(el, name, fb) {
  if (!el.hasAttribute(name)) return fb;
  const v = (el.getAttribute(name) || '').trim().toLowerCase();
  if (v === '' || v === 'true') return true;
  if (v === 'false') return false;
  return fb;
}

function strAttr(el, name, fb = '') {
  return el.hasAttribute(name) ? (el.getAttribute(name) || fb).trim() : fb;
}

function prefersReducedMotion() {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function getBp() {
  const w = window.innerWidth;
  if (w <= BP.mobileMax) return 'mobile';
  if (w <= BP.tabletMax) return 'tablet';
  return 'desktop';
}

function isInViewport(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < (window.innerHeight || d.documentElement.clientHeight);
}

function toPxLength(value, scopeEl) {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v || v === 'auto') return 0;
  if (v.endsWith('px'))  return parseFloat(v) || 0;
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  if (v.endsWith('rem')) return n * (parseFloat(getComputedStyle(d.documentElement).fontSize) || 16);
  if (v.endsWith('em'))  return n * (parseFloat(getComputedStyle(scopeEl || d.body).fontSize) || 16);
  return n;
}

// ── Discovery ─────────────────────────────────────────────────────────────────

function getPotentialSliders(root = d) {
  const set = new Set();
  root.querySelectorAll('.splide').forEach(el => set.add(el));
  root.querySelectorAll("[data-splide-root='true']").forEach(el => set.add(el));
  root.querySelectorAll('[data-slider-from-breakpoint],[data-slider-until-breakpoint]').forEach(el => set.add(el));
  return [...set];
}

// ── Activation rules ──────────────────────────────────────────────────────────

function shouldActivate(slider, bp) {
  if (boolAttr(slider, 'data-force-active', false)) return true;
  const idx   = BP_ORDER.indexOf(bp);
  const from  = strAttr(slider, 'data-slider-from-breakpoint').toLowerCase();
  const until = strAttr(slider, 'data-slider-until-breakpoint').toLowerCase();
  if (from  && BP_ORDER.includes(from)  && idx > BP_ORDER.indexOf(from))  return false;
  if (until && BP_ORDER.includes(until) && idx < BP_ORDER.indexOf(until)) return false;
  return true;
}

// ── Class management ──────────────────────────────────────────────────────────

function getNodes(slider) {
  return {
    track:  slider.querySelector("[data-splide-role='track']"),
    list:   slider.querySelector("[data-splide-role='list']"),
    slides: slider.querySelectorAll("[data-splide-role='slide']"),
  };
}

function snapshotBuckets(el) {
  if (!el || (el.hasAttribute(ATTR_SPLIDE_ONLY) && el.hasAttribute(ATTR_OTHER_ONLY))) return;
  const tokens = String(el.className || '').trim().split(/\s+/).filter(Boolean);
  el.setAttribute(ATTR_SPLIDE_ONLY, tokens.filter(c =>  c.toLowerCase().includes('splide')).join(' ').trim());
  el.setAttribute(ATTR_OTHER_ONLY,  tokens.filter(c => !c.toLowerCase().includes('splide') && !c.startsWith('is-')).join(' ').trim());
}

function bootstrapSlider(slider) {
  if (!slider) return false;
  slider.setAttribute('data-splide-root', 'true');

  const track  = slider.querySelector('.splide__track')  || slider.querySelector("[data-splide-role='track']");
  const list   = slider.querySelector('.splide__list')   || slider.querySelector("[data-splide-role='list']");
  const slides = slider.querySelectorAll('.splide__slide').length
    ? slider.querySelectorAll('.splide__slide')
    : slider.querySelectorAll("[data-splide-role='slide']");

  if (!track || !list || !slides.length) return false;

  track.setAttribute('data-splide-role', 'track');
  list.setAttribute('data-splide-role',  'list');
  slides.forEach(s => s.setAttribute('data-splide-role', 'slide'));
  [slider, track, list, ...slides].forEach(snapshotBuckets);
  return true;
}

const OVERFLOW_EXT = '200vw';

function applyOverflowMode(slider, active) {
  const { track } = getNodes(slider);
  if (!track) return;
  if (!active) { slider.style.overflow = ''; track.style.overflow = ''; track.style.clipPath = ''; return; }
  const mode = strAttr(slider, 'data-slider-overflow', 'visible').toLowerCase();
  slider.style.overflow = mode === 'hidden' ? 'hidden' : 'visible';
  if (mode === 'hidden') { track.style.overflow = 'hidden'; track.style.clipPath = ''; return; }
  track.style.overflow = 'visible';
  if (mode === 'left')  { track.style.clipPath = `inset(0 calc(-1 * ${OVERFLOW_EXT}) 0 0)`; return; }
  if (mode === 'right') { track.style.clipPath = `inset(0 0 0 calc(-1 * ${OVERFLOW_EXT}))`; return; }
  track.style.clipPath = '';
}

function setActiveClasses(slider, active) {
  const { track, list, slides } = getNodes(slider);
  [slider, track, list, ...slides].filter(Boolean).forEach(el => {
    snapshotBuckets(el);
    el.className = active ? (el.getAttribute(ATTR_SPLIDE_ONLY) || '') : (el.getAttribute(ATTR_OTHER_ONLY) || '');
    el.classList.remove('is-active','is-prev','is-next','is-visible','is-clone','is-initialized','is-rendered');
  });
  const arrows     = slider.querySelector('.splide__arrows');
  const pagination = slider.querySelector('.splide__pagination');
  if (arrows)     arrows.style.display     = active ? '' : 'none';
  if (pagination) pagination.style.display = active ? '' : 'none';
  applyOverflowMode(slider, active);
}

function clearStaticStyles(slider) {
  const { list } = getNodes(slider);
  if (!list) return;
  list.style.display = list.style.justifyContent = list.style.gap = '';
}

// ── Settings ──────────────────────────────────────────────────────────────────

function parsePerPageMap(slider) {
  const parts = strAttr(slider, 'data-slide-per-page').split(',').map(v => v.trim().toLowerCase());
  const parse = (v, fb) => !v ? fb : v === 'auto' ? 'auto' : num(v, fb);
  return {
    desktop: parse(parts[0], DEFAULTS.perPage.desktop),
    tablet:  parse(parts[1], DEFAULTS.perPage.tablet),
    mobile:  parse(parts[2], DEFAULTS.perPage.mobile),
  };
}

function getMaxChildMinWidthPx(slider) {
  const { slides } = getNodes(slider);
  let max = 0;
  slides.forEach(slide => {
    const child = slide?.firstElementChild;
    if (!child) return;
    const px = toPxLength(getComputedStyle(child).minWidth, child);
    if (px > max) max = px;
  });
  return max;
}

function applyMinWidthClamp(slider, settings) {
  if (settings.autoWidth || !Number.isFinite(settings.perPage) || settings.perPage <= 0) return;
  const { track } = getNodes(slider);
  const trackW   = track?.getBoundingClientRect().width || slider.getBoundingClientRect().width;
  if (!trackW) return;
  const gapPx   = toPxLength(settings.gap || DEFAULTS.gap, slider);
  const minChild = getMaxChildMinWidthPx(slider);
  if (minChild <= 0) return;
  settings.perPage = Math.max(1, Math.min(settings.perPage, Math.floor((trackW + gapPx) / (minChild + gapPx))));
}

function isStatic(slider, bp) {
  if (boolAttr(slider, 'data-force-slider', false)) return false;
  const pp = parsePerPageMap(slider)[bp];
  if (pp === 'auto') return false;
  return getNodes(slider).slides.length <= pp;
}

function buildSettings(slider) {
  const perPageMap    = parsePerPageMap(slider);
  const gap           = strAttr(slider, 'data-gap', DEFAULTS.gap);
  const transition    = strAttr(slider, 'data-transition', strAttr(slider, 'data-transition-type', 'slide')).toLowerCase();
  const loopScroll    = boolAttr(slider, 'data-loop-scroll', true);
  const disableInf    = boolAttr(slider, 'data-disable-infinite', false);
  const perMove       = int(slider.getAttribute('data-slide-per-move'), DEFAULTS.perMove);
  const autoplay      = boolAttr(slider, 'data-autoplay', true);
  const interval      = int(slider.getAttribute('data-autoplay-speed'), DEFAULTS.interval);
  const dragRaw       = strAttr(slider, 'data-drag', 'true').toLowerCase();
  const drag          = dragRaw === 'none' || dragRaw === 'false' ? false : dragRaw === 'free' ? 'free' : true;
  const pauseOn       = strAttr(slider, 'data-pause-on', 'all').toLowerCase();
  const speed         = int(slider.getAttribute('data-speed'), DEFAULTS.speed);
  const rewindSpeed   = int(slider.getAttribute('data-rewind-speed'), DEFAULTS.rewindSpeed);
  const autoScrollSpd = num(slider.getAttribute('data-autoscroll-speed'), 0);
  const reduce        = prefersReducedMotion() && !boolAttr(slider, 'data-ignore-reduced-motion', false);
  const focusAttr     = slider.getAttribute('data-focus');

  const type = transition === 'fade' ? 'fade' : (disableInf || !loopScroll) ? 'slide' : 'loop';

  const breakpoints = {
    [BP.tabletMax]: perPageMap.tablet === 'auto' ? { autoWidth: true } : { perPage: perPageMap.tablet },
    [BP.mobileMax]: perPageMap.mobile === 'auto' ? { autoWidth: true } : { perPage: perPageMap.mobile },
  };

  const dynamic = {
    type, perMove, gap, speed, rewindSpeed, drag,
    snap:        boolAttr(slider, 'data-snap', false),
    arrows:      boolAttr(slider, 'data-arrows', false),
    pagination:  boolAttr(slider, 'data-pagination', false),
    easing:      strAttr(slider, 'data-easing', 'linear'),
    flickPower:  int(slider.getAttribute('data-flick-power'),    int(slider.getAttribute('data-flick-max-pages'), 1) * 100),
    flickMaxPages: int(slider.getAttribute('data-flick-max-pages'), 1),
    focus:       focusAttr == null ? 0 : isNaN(focusAttr) ? focusAttr : num(focusAttr, 0),
    autoplay:    !reduce && autoplay,
    interval,
    pauseOnHover: pauseOn === 'all' || pauseOn.includes('hover'),
    pauseOnFocus: pauseOn === 'all' || pauseOn.includes('focus'),
    breakpoints,
    ...(perPageMap.desktop === 'auto' ? { autoWidth: true } : { perPage: perPageMap.desktop }),
    ...(!reduce && autoScrollSpd > 0 ? { autoScroll: { speed: autoScrollSpd } } : {}),
  };

  const statik = {
    type: 'slide', arrows: false, pagination: false, drag: false, snap: false,
    gap, speed, rewindSpeed,
    perPage: perPageMap.desktop === 'auto' ? 1 : perPageMap.desktop,
  };

  return { dynamic, static: statik, wantsAutoScroll: !reduce && autoScrollSpd > 0 };
}

// ── Asset loader ──────────────────────────────────────────────────────────────

async function ensureAssets() {
  if (STATE.assetsPromise) return STATE.assetsPromise;
  if (typeof window.Splide !== 'undefined') { STATE.assetsPromise = Promise.resolve(true); return STATE.assetsPromise; }

  const needsAutoScroll = getPotentialSliders().some(s => num(s.getAttribute('data-autoscroll-speed'), 0) > 0);

  STATE.assetsPromise = (async () => {
    await loadSplideCSS();
    await loadSplide();
    if (needsAutoScroll) await loadSplideAutoScroll();
    return typeof window.Splide !== 'undefined';
  })();

  return STATE.assetsPromise;
}

// ── Init / Destroy ────────────────────────────────────────────────────────────

function destroySlider(slider) {
  const inst = STATE.instances.get(slider);
  if (inst) { try { inst.destroy(true); } catch (e) {} STATE.instances.delete(slider); }
  slider.classList.remove('splide--inactive');
  clearStaticStyles(slider);
}

function initSlider(slider) {
  if (!bootstrapSlider(slider)) return;
  const bp     = getBp();
  const active = shouldActivate(slider, bp);
  setActiveClasses(slider, active);
  if (!active) { destroySlider(slider); return; }
  if (STATE.instances.get(slider)) return;

  const { dynamic, static: statik, wantsAutoScroll } = buildSettings(slider);

  if (isStatic(slider, bp)) {
    destroySlider(slider);
    slider.classList.add('splide--inactive');
    applyMinWidthClamp(slider, statik);
    const splide = new window.Splide(slider, statik);
    splide.on('mounted', () => {
      if (!boolAttr(slider, 'data-static-center', true)) return;
      const { list } = getNodes(slider);
      if (!list) return;
      list.style.display = 'flex';
      list.style.justifyContent = 'center';
      list.style.gap = statik.gap || DEFAULTS.gap;
    });
    splide.mount();
    STATE.instances.set(slider, splide);
    return;
  }

  applyMinWidthClamp(slider, dynamic);
  const splide = new window.Splide(slider, dynamic);
  wantsAutoScroll && window.splide?.Extensions
    ? splide.mount(window.splide.Extensions)
    : splide.mount();
  STATE.instances.set(slider, splide);
}

function applyBpToAll(bp) {
  getPotentialSliders().forEach(slider => {
    if (!bootstrapSlider(slider)) return;
    setActiveClasses(slider, shouldActivate(slider, bp));
    if (!shouldActivate(slider, bp)) destroySlider(slider);
  });
}

// ── Resize ────────────────────────────────────────────────────────────────────

async function handleResize() {
  if (STATE.resizingLock) return;
  STATE.resizingLock = true;
  try {
    const newBp = getBp();
    if (newBp === STATE.currentBp) return;
    const prevBp = STATE.currentBp;
    STATE.currentBp = newBp;
    applyBpToAll(newBp);
    for (const slider of getPotentialSliders()) {
      if (!bootstrapSlider(slider)) continue;
      const changed = shouldActivate(slider, prevBp) !== shouldActivate(slider, newBp)
                   || isStatic(slider, prevBp) !== isStatic(slider, newBp);
      if (!changed) continue;
      destroySlider(slider);
      if (shouldActivate(slider, newBp) && isInViewport(slider)) {
        const ok = await ensureAssets();
        if (ok) initSlider(slider);
      }
    }
  } finally {
    setTimeout(() => { STATE.resizingLock = false; }, 0);
  }
}

function bindResize() {
  if (STATE.resizeBound) return;
  STATE.resizeBound = true;
  let t;
  window.addEventListener('resize', () => {
    if (STATE.resizingLock) return;
    const w = Math.round(window.visualViewport?.width || window.innerWidth);
    if (STATE.lastResizeWidth === w) return;
    STATE.lastResizeWidth = w;
    clearTimeout(t);
    t = setTimeout(handleResize, 150);
  });
}

// ── IntersectionObserver ──────────────────────────────────────────────────────

function ensureObserver() {
  if (STATE.observer) return STATE.observer;
  if (!('IntersectionObserver' in window)) return null;
  STATE.observer = new IntersectionObserver(async entries => {
    for (const entry of entries) {
      const slider = entry.target;
      if (!entry.isIntersecting || !bootstrapSlider(slider)) continue;
      const active = shouldActivate(slider, getBp());
      setActiveClasses(slider, active);
      if (!active) continue;
      try { STATE.observer.unobserve(slider); } catch (e) {}
      const ok = await ensureAssets();
      if (ok) initSlider(slider);
    }
  }, { rootMargin: '0px' });
  return STATE.observer;
}

function observeSliders(root = d) {
  const obs = ensureObserver();
  if (!obs) return;
  getPotentialSliders(root).forEach(slider => {
    if (STATE.observed.has(slider)) return;
    STATE.observed.add(slider);
    obs.observe(slider);
  });
}

async function boot() {
  STATE.currentBp = getBp();
  applyBpToAll(STATE.currentBp);
  observeSliders();
  bindResize();
  if (!('IntersectionObserver' in window)) {
    const ok = await ensureAssets();
    if (ok) getPotentialSliders().forEach(initSlider);
  }
}

// ── Feature entry point ───────────────────────────────────────────────────────

let booted = false;

export default async function slider() {
  if (booted) return;
  booted = true;
  boot();
}
